import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from './Users.module.scss';
import get from '../../../services/axios/get';
import del from '../../../services/axios/delete';
import patch from '../../../services/axios/patch';
import { useAppDispatch } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import post from '../../../services/axios/post';
import { Col, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Table } from 'reactstrap';
import { CgMenu } from 'react-icons/cg';
import Loader from '../../UI/Loader/Loader';
import { FaPlus } from 'react-icons/fa';
import Modal from '../../../containers/Modal/Modal';
import UserForm from './UserForm/UserForm';
import User from '../../../models/user';
import Alert from '../../UI/Alert/Alert';
import { Entity } from '../../Loads/LoadForm/LoadForm';
import PluginDepot from '../../../models/pluginDepot';

interface LoadPoint {
  id: string;
  alias: string;
}

interface EntityState<T> {
  data: T[];
  isLoading: boolean;
}

interface UsersProps { };

const Users: React.FC<UsersProps> = (props) => {
  const dispatch = useAppDispatch()
  const isMounted = useRef(true);
  const [users, setUser] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>({});
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [clients, setClients] = useState<EntityState<Entity>>({ data: [], isLoading: true });
  const [loadPoints, setLoadPoints] = useState<EntityState<LoadPoint>>({ data: [], isLoading: true });
  const [pluginDepots, setPluginDepots] = useState<EntityState<PluginDepot>>({ data: [], isLoading: true });

  const createNewUser = (user: Omit<User, 'id' | 'isSuperAdmin' | 'activationCode'>) => {
    if (user.userType === 'driver' && user.ownerId) {
      const ownerUser = users.find((u: User) => u.id === user.ownerId);
      if (ownerUser) {
        user = {
          ...user,
          ownerName: `${ownerUser.firstName} ${ownerUser.lastName}`
        };
      }
    }

    if (user.ownerId === null) {
      delete user.ownerId;
    }

    post(
      '/user',
      user,
      () => {
        if (isMounted) {
          setShowCreateUser(false);
          setIsLoading(true);
          getUsers();
        }
      },
      (err) => {
        if (isMounted) {
          dispatch(customAlert(true, err));
          setIsLoading(false);
          setShowCreateUser(false);
        }
      }
    )
  }

  const generateActivationCode = (userId: string) => {
    setIsLoading(true);
    post(
      `/user/activation-code`,
      { userId },
      () => {
        if (isMounted) {
          setIsLoading(true);
          getUsers();
        }
      },
      (err) => {
        if (isMounted) {
          dispatch(customAlert(true, err));
          setIsLoading(false);
        }
      }
    )
  }

  const deleteUser = (userId: string) => {
    del(
      `/user/${userId}`,
      {},
      () => {
        if (isMounted) {
          setIsLoading(true);
          getUsers();
        }
      },
      (err) => {
        if (isMounted) {
          dispatch(customAlert(true, err));
          setIsLoading(false);
        }
      }
    )
  };

  const editUser = (userId: string, userData: Omit<User, 'id' | 'isSuperAdmin' | 'activationCode'>) => {
    if (userData.userType === 'driver' && userData.ownerId) {
      const ownerUser = users.find((user: User) => user.id === userData.ownerId);
      if (ownerUser) {
        userData = {
          ...userData,
          ownerName: `${ownerUser.firstName} ${ownerUser.lastName}`
        };
      }
    }

    if (userData.ownerId === null) {
      delete userData.ownerId;
    }

    patch(
      `/user/${userId}`,
      userData,
      () => {
        if (isMounted) {
          setShowEditUser(false);
          setIsLoading(true);
          getUsers();
        }
      },
      (err) => {
        if (isMounted) {
          dispatch(customAlert(true, err));
          setIsLoading(false);
        }
      }
    )
  };

  const getUsers = useCallback(
    () => {
      get(
        '/user',
        {},
        (res) => {
          if (isMounted) {
            setUser(res.data);
            setIsLoading(false)
          }
        },
        () => {
          setIsLoading(false)
        });
    },
    []
  );

  const getClients = useCallback(
    () => {
      get(
        '/client',
        {},
        (res) => {
          if (isMounted.current) {
            setClients({ data: res.data, isLoading: false });
          }
        },
        () => {
          if (isMounted.current) {

          }
        });
    },
    []
  );

  const getLoadPoints = useCallback(
    () => {
      get(
        '/load-point',
        {},
        (res) => {
          if (isMounted.current) {
            setLoadPoints({ data: res.data, isLoading: false });
          }
        },
        () => {
          if (isMounted.current) {
            setLoadPoints(prev => ({ ...prev, isLoading: false }));
          }
        });
    },
    []
  );

  const getPluginDepots = useCallback(
    () => {
      get(
        '/depot',
        { isPlugin: true },
        (res) => {
          if (isMounted.current) {
            setPluginDepots({ data: res.data, isLoading: false });
          }
        },
        () => {
          if (isMounted.current) {
            setPluginDepots(prev => ({ ...prev, isLoading: false }));
          }
        });
    },
    []
  );

  const toggleDropdown = (userId: string) => {
    setDropdownOpen(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setShowEditUser(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const renderUsers = () => {
    return users.map((user: User, index: number) => {
      const ownerInfo = user.userType === 'driver' && user.ownerId && !user.ownerName ? 
        users.find((u: User) => u.id === user.ownerId) : null;
      const ownerName = user.ownerName || (ownerInfo ? `${ownerInfo.firstName} ${ownerInfo.lastName}` : null);

      return (
        <tr key={user.id}>
          <td>{user.firstName} {user.lastName}{ownerName && ` (${ownerName})`}</td>
          <td>{user.userType}</td>
          <td>{user.email}</td>
          <td>{user.phoneNumber}</td>
          <td>{user.clientId ? clients.data.find((client: Entity) => client.id === user.clientId)?.alias : '-'}</td>
          <td>{user.activationCode}</td>
          <td className={classes['menu']}>
            <Dropdown isOpen={dropdownOpen[user.id] || false} toggle={() => toggleDropdown(user.id)} direction={'down'} className={classes['dropdown']}>
              <DropdownToggle><CgMenu size={'16'} /></DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => handleEditClick(user)}>Edit</DropdownItem>
                <DropdownItem onClick={() => generateActivationCode(user.id)}>Invite</DropdownItem>
                <DropdownItem onClick={() => handleDeleteClick(user)}>Delete</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </td>
        </tr>
      )
    })
  }

  useEffect(() => {
    getUsers();
    getClients();
    getLoadPoints();
    getPluginDepots();

    return () => {
      isMounted.current = false;
    }
  }, [getUsers, getClients, getLoadPoints, getPluginDepots])

  return (
    <Container>
      {showCreateUser ? (
        <Modal close={() => setShowCreateUser(false)}>
          <UserForm close={() => setShowCreateUser(false)} createUser={createNewUser} user={null} clients={clients} users={users} loadPoints={loadPoints} pluginDepots={pluginDepots} />
        </Modal>
      ) : null}

      {showEditUser && selectedUser ? (
        <Modal close={() => setShowEditUser(false)}>
          <UserForm
            close={() => setShowEditUser(false)}
            createUser={(userData) => editUser(selectedUser.id, userData)}
            user={selectedUser}
            clients={clients}
            users={users}
            loadPoints={loadPoints}
            pluginDepots={pluginDepots}
          />
        </Modal>
      ) : null}

      {showDeleteConfirm && userToDelete ? (
        <Alert
          confirm={(confirmation) => {
            if (confirmation && userToDelete !== null) {
              deleteUser(userToDelete.id);
              setUserToDelete(null);
            } else {
              setUserToDelete(null);
            }
          }}
          message={
            <div>
              Are you sure you want to delete the user named {userToDelete.firstName} {userToDelete.lastName}?
            </div>
          }
          isOpen={userToDelete !== null}
          isConfirm
        />
      ) : null}

      <Row>
        <Col xs='12'>
          <div className={classes['create-new-user']}>
            <div onClick={() => setShowCreateUser(true)}>
              <FaPlus /> Create New User
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className={classes['users-box']}>
            <div className={isLoading ? classes['loader-container'] : classes['users-list']}>
              {isLoading ? (
                <Loader size='large' />
              ) : (
                <Table striped hover responsive className={classes['users-table']}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>User Type</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Client</th>
                      <th>Activation Code</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderUsers()}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Users;